import { useEffect, useState } from "react";
import { AlertTriangle, ArrowLeftRight, Download, FileUp, Globe2, Search, ShieldCheck, Trash2, UploadCloud } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";

const card = "rounded-2xl border border-adminborder bg-admincard";
const darkInput = "w-full rounded-lg border border-adminborder bg-adminbg px-3.5 py-2.5 text-sm text-slate-200 outline-none focus:border-saffron";
const th = "px-4 py-3";
const STATUS_STYLE = {
  ACTIVE: "bg-emerald-500/15 text-emerald-400",
  DRAFT: "bg-amber-500/15 text-amber-400",
  ARCHIVED: "bg-slate-500/15 text-slate-400",
};
const inr = (n) => n == null ? "—" : `₹${Number(n).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

export default function RateCards() {
  const [carrier, setCarrier] = useState("DHL");
  const cname = carrier === "FEDEX" ? "FedEx" : carrier === "UPS" ? "UPS" : "DHL";
  const zoneRange = carrier === "FEDEX" ? "A–N" : carrier === "UPS" ? "1–9 + ISO columns" : "1–14";
  const [versions, setVersions] = useState([]);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [effDate, setEffDate] = useState("");
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [compare, setCompare] = useState(null);
  const [compareWith, setCompareWith] = useState("");
  const [rules, setRules] = useState(null);
  const [rulesOpen, setRulesOpen] = useState(false);
  const [savingRules, setSavingRules] = useState(false);
  const [q, setQ] = useState({ country: "", weight: "" });
  const [qResult, setQResult] = useState(null);
  const [qBusy, setQBusy] = useState(false);
  const [busy, setBusy] = useState("");
  const [surges, setSurges] = useState([]);
  const [surgeFilter, setSurgeFilter] = useState("");
  const [surgeFile, setSurgeFile] = useState(null);
  const [surgeUploading, setSurgeUploading] = useState(false);
  const [surgeEdit, setSurgeEdit] = useState({});
  const [surgeSaving, setSurgeSaving] = useState("");
  const [health, setHealth] = useState({});
  const [healthOpen, setHealthOpen] = useState(false);

  const loadHealth = () => Promise.all(["DHL", "FEDEX", "UPS"].map((c) =>
    api.get("/admin/rate-cards/health", { params: { carrier: c } })
       .then((r) => [c, r.data]).catch(() => [c, null])
  )).then((entries) => setHealth(Object.fromEntries(entries.filter(([, v]) => v))));

  const load = () => {
    api.get("/admin/rate-cards", { params: { carrier } }).then((r) => setVersions(r.data)).catch(() => {});
    loadHealth();
  };
  const loadSurges = () => api.get("/admin/rate-cards/surcharges", { params: { carrier: "UPS" } }).then((r) => setSurges(r.data)).catch(() => {});
  useEffect(() => {
    setPreview(null); setCompare(null); setCompareWith(""); setQResult(null);
    setSurgeFilter(""); setSurgeFile(null); setSurgeEdit({}); setHealthOpen(false);
    load();
    api.get(`/admin/rate-cards/rules/${carrier}`).then((r) => setRules(r.data)).catch(() => {});
    if (carrier === "UPS") loadSurges(); else setSurges([]);
  }, [carrier]);

  const uploadSurge = async () => {
    if (!surgeFile) { toast.error("Choose the UPS war surcharge Excel first"); return; }
    setSurgeUploading(true);
    try {
      const fd = new FormData();
      fd.append("carrier", "UPS");
      fd.append("file", surgeFile);
      const r = await api.post("/admin/rate-cards/surcharges/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`War surcharge imported — ${r.data.count} destinations now price with the surge`);
      setSurgeFile(null);
      loadSurges();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSurgeUploading(false); }
  };

  const saveSurge = async (s) => {
    const val = parseFloat(surgeEdit[s.id]);
    if (!(val >= 0)) { toast.error("Enter a valid ₹/KG value"); return; }
    setSurgeSaving(s.id);
    try {
      await api.put(`/admin/rate-cards/surcharges/${s.id}`, { surge_with_gst: val });
      toast.success(`${s.country_name || s.iso_code}: surge updated to ₹${val}/KG`);
      setSurgeEdit((m) => { const n = { ...m }; delete n[s.id]; return n; });
      loadSurges();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSurgeSaving(""); }
  };

  const upload = async () => {
    if (!file) { toast.error("Choose a PDF / Excel / CSV rate card first"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("carrier", carrier);
      fd.append("effective_date", effDate);
      fd.append("file", file);
      const r = await api.post("/admin/rate-cards/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Parsed as ${r.data.version} — review the import preview`);
      setUploadOpen(false);
      setFile(null); setEffDate("");
      load();
      openPreview(r.data.id);
    } catch (e) { toast.error(apiError(e)); }
    finally { setUploading(false); }
  };

  const openPreview = async (id) => {
    try {
      const r = await api.get(`/admin/rate-cards/${id}/preview`);
      setPreview(r.data);
    } catch (e) { toast.error(apiError(e)); }
  };

  const publish = async (id, force = false) => {
    setBusy(id);
    try {
      await api.post(`/admin/rate-cards/${id}/publish`, null, { params: { force } });
      toast.success(`Rate card published — it is now the ACTIVE ${cname} price source`);
      setPreview(null);
      load();
    } catch (e) {
      if (e.response?.status === 409) {
        toast.error("Warnings detected — use PUBLISH ANYWAY after reviewing");
      } else if (e.response?.status === 400 && e.response?.data?.detail?.errors) {
        toast.error("Import errors must be resolved before publishing");
      } else toast.error(apiError(e));
    } finally { setBusy(""); }
  };

  const archive = async (id) => {
    if (!window.confirm("Archive this rate card version?")) return;
    try { await api.post(`/admin/rate-cards/${id}/archive`); toast.success("Archived"); load(); setPreview(null); }
    catch (e) { toast.error(apiError(e)); }
  };

  const removeVersion = async (v) => {
    if (!window.confirm(`Permanently delete ${v.version} (${v.file_name})? Its countries and rates will be removed.`)) return;
    try { await api.delete(`/admin/rate-cards/${v.id}`); toast.success(`${v.version} deleted`); load(); setPreview(null); }
    catch (e) { toast.error(apiError(e)); }
  };

  const download = async (v) => {
    try {
      const res = await api.get(`/admin/rate-cards/${v.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = v.file_name || "rate-card"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiError(e)); }
  };

  const runCompare = async (newId, oldId) => {
    if (!oldId) { toast.error("Pick a version to compare against"); return; }
    try {
      const r = await api.get("/admin/rate-cards/compare", { params: { old: oldId, new: newId } });
      setCompare(r.data);
    } catch (e) { toast.error(apiError(e)); }
  };

  const saveRules = async () => {
    setSavingRules(true);
    try {
      const clean = {
        table_max_weight: parseFloat(rules.table_max_weight),
        increment_tiers: rules.increment_tiers.map((t) => ({
          from: parseFloat(t.from), to: t.to === "" || t.to == null ? null : parseFloat(t.to), key_weight: parseFloat(t.key_weight) })),
        markup_tiers: rules.markup_tiers.map((t) => ({
          from: parseFloat(t.from), to: t.to === "" || t.to == null ? null : parseFloat(t.to), type: t.type, amount: parseFloat(t.amount) })),
      };
      const r = await api.put(`/admin/rate-cards/rules/${carrier}`, clean);
      setRules(r.data);
      toast.success(`${cname} pricing rules saved — calculations update immediately`);
    } catch (e) { toast.error(apiError(e)); }
    finally { setSavingRules(false); }
  };

  const search = async () => {
    if (!q.country.trim() || !(parseFloat(q.weight) > 0)) { toast.error("Enter a country and weight"); return; }
    setQBusy(true); setQResult(null);
    try {
      const r = await api.get("/admin/rate-cards/search", { params: { carrier, country: q.country.trim(), weight: parseFloat(q.weight) } });
      setQResult(r.data);
    } catch (e) { toast.error(apiError(e)); }
    finally { setQBusy(false); }
  };

  const active = versions.find((v) => v.status === "ACTIVE");
  const val = preview?.version?.validation || { errors: [], warnings: [] };

  return (
    <div className="space-y-6" data-testid="rate-cards-panel">
      <div className="flex gap-2" data-testid="rc-carrier-tabs">
        {[["DHL", "DHL"], ["FEDEX", "FedEx"], ["UPS", "UPS"]].map(([code, label]) => (
          <button key={code} onClick={() => setCarrier(code)} data-testid={`rc-carrier-${code.toLowerCase()}-tab`}
                  className={`flex items-center rounded-full px-5 py-2 text-xs font-bold transition-colors ${carrier === code ? "bg-saffron text-white" : "border border-adminborder text-slate-400 hover:text-white"}`}>
            {label}
            {health[code] && (health[code].clean
              ? <ShieldCheck className="ml-1.5 h-3.5 w-3.5 text-emerald-400" data-testid={`rc-health-ok-${code.toLowerCase()}`} />
              : <span data-testid={`rc-health-dot-${code.toLowerCase()}`} title={`${health[code].flagged.length} country names to review`}
                      className="ml-1.5 inline-flex items-center rounded-full bg-amber-500/25 px-1.5 py-0.5 text-[9px] font-bold text-amber-400">
                  {health[code].flagged.length}
                </span>)}
          </button>
        ))}
      </div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-heading text-xl font-bold text-white">{cname} Rate Cards</h2>
          <p className="mt-0.5 text-sm text-slate-500">Upload the official {cname} rate card (PDF / Excel / CSV) — the engine prices {cname} from the ACTIVE version automatically. Zones {zoneRange}.</p>
        </div>
        <button onClick={() => setUploadOpen(true)} data-testid={`${carrier.toLowerCase()}-upload-ratecard-btn`}
                className="flex items-center gap-2 rounded-full bg-saffron px-5 py-2.5 text-sm font-bold text-white hover:bg-saffron-hover">
          <UploadCloud className="h-4 w-4" /> Upload {cname} Rate Card
        </button>
      </div>

      {health[carrier] && !health[carrier].clean && (
        <div className={`${card} border-amber-500/40 bg-amber-500/5 p-4`} data-testid="rc-health-panel">
          <button onClick={() => setHealthOpen((o) => !o)} data-testid="rc-health-toggle" className="flex w-full items-center justify-between text-left">
            <span className="flex items-center gap-2 text-xs font-bold text-amber-400">
              <AlertTriangle className="h-4 w-4" /> Data health — {health[carrier].flagged.length} country {health[carrier].flagged.length === 1 ? "name" : "names"} to review on the ACTIVE card ({health[carrier].version})
            </span>
            <span className="text-[10px] font-bold uppercase tracking-wider text-amber-500/70">{healthOpen ? "Hide" : "Review"}</span>
          </button>
          {healthOpen && (
            <div className="mt-3 max-h-48 overflow-auto rounded-xl border border-amber-500/20" data-testid="rc-health-list">
              {health[carrier].flagged.map((f, i) => (
                <div key={i} className="flex items-center justify-between gap-3 border-b border-amber-500/10 px-3 py-2 text-xs last:border-0" data-testid={`rc-health-item-${i}`}>
                  <span className="font-semibold text-slate-200">{f.country_name} <span className="font-normal text-slate-500">({f.iso_code || "—"} · Zone {f.zone})</span></span>
                  <span className="text-amber-400/80">{f.reason}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {active && (
        <div className={`${card} flex flex-wrap items-center gap-x-6 gap-y-2 p-4`} data-testid="active-ratecard-banner">
          <span className="flex items-center gap-2 text-sm font-bold text-white"><Globe2 className="h-4 w-4 text-saffron" /> Active {cname} Rate Card</span>
          <span className="text-xs text-slate-400">Version <b className="text-slate-200">{active.version}</b></span>
          <span className="text-xs text-slate-400">Effective <b className="text-slate-200">{active.effective_date}</b></span>
          <span className="text-xs text-slate-400">{active.total_countries} countries · {active.total_zones} zones · {active.total_rates} rates</span>
          <button onClick={() => openPreview(active.id)} data-testid="active-ratecard-view" className="text-xs font-bold text-saffron hover:underline">View rates</button>
        </div>
      )}

      <div className={`${card} overflow-x-auto`}>
        <table className="w-full text-sm" data-testid="ratecard-versions-table">
          <thead>
            <tr className="border-b border-adminborder text-left text-xs uppercase tracking-wider text-slate-500">
              <th className={th}>Version</th><th className={th}>File</th><th className={th}>Effective</th>
              <th className={th}>Uploaded</th><th className={th}>Countries</th><th className={th}>Rates</th>
              <th className={th}>Status</th><th className={th}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {versions.map((v) => (
              <tr key={v.id} className="border-b border-adminborder/60 last:border-0 hover:bg-white/5" data-testid={`rc-version-row-${v.id}`}>
                <td className={`${th} font-mono text-xs font-bold text-white`}>{v.version}</td>
                <td className={`${th} max-w-48 truncate text-xs text-slate-400`}>{v.file_name}</td>
                <td className={`${th} text-xs`}>{v.effective_date}</td>
                <td className={`${th} text-xs text-slate-400`}>{(v.uploaded_at || "").slice(0, 10)}<br />{v.uploaded_by}</td>
                <td className={`${th} text-xs`}>{v.total_countries}</td>
                <td className={`${th} text-xs`}>{v.total_rates}</td>
                <td className={th}>
                  <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${STATUS_STYLE[v.status] || ""}`} data-testid={`rc-status-${v.id}`}>{v.status}</span>
                </td>
                <td className={th}>
                  <div className="flex flex-wrap gap-1.5">
                    <button onClick={() => openPreview(v.id)} data-testid={`rc-view-${v.id}`}
                            className="rounded-lg border border-adminborder px-2.5 py-1 text-[10px] font-bold text-slate-300 hover:border-saffron hover:text-saffron">View</button>
                    <button onClick={() => download(v)} data-testid={`rc-download-${v.id}`}
                            className="rounded-lg border border-adminborder px-2.5 py-1 text-[10px] font-bold text-slate-300 hover:border-saffron hover:text-saffron"><Download className="inline h-3 w-3" /></button>
                    {v.status !== "ACTIVE" && (
                      <button onClick={() => publish(v.id)} disabled={busy === v.id} data-testid={`rc-publish-${v.id}`}
                              className="rounded-lg border border-emerald-500/40 px-2.5 py-1 text-[10px] font-bold text-emerald-400 hover:bg-emerald-500/10 disabled:opacity-50">Publish</button>
                    )}
                    {v.status === "DRAFT" && (
                      <button onClick={() => archive(v.id)} data-testid={`rc-archive-${v.id}`}
                              className="rounded-lg border border-red-500/40 px-2.5 py-1 text-[10px] font-bold text-red-400 hover:bg-red-500/10">Archive</button>
                    )}
                    {v.status !== "ACTIVE" && (
                      <button onClick={() => removeVersion(v)} data-testid={`rc-delete-${v.id}`}
                              className="rounded-lg border border-red-500/40 px-2.5 py-1 text-[10px] font-bold text-red-400 hover:bg-red-500/10"><Trash2 className="inline h-3 w-3" /></button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {!versions.length && (
              <tr><td colSpan={8} className="px-4 py-12 text-center text-slate-500" data-testid="rc-empty">No {cname} rate cards yet — upload the first one to activate the rate-card engine.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {versions.length >= 2 && (
        <div className={`${card} p-4`} data-testid="rc-compare-card">
          <p className="mb-2 text-[11px] font-bold uppercase tracking-wider text-slate-500">Compare rate cards</p>
          <div className="flex flex-wrap items-center gap-2">
            <select className={`${darkInput} max-w-64`} data-testid="rc-compare-new"
                    value={compare ? compare.new.id : ""} disabled onChange={() => {}}>
              <option value="">{compare ? `${compare.new.version} (new)` : "Pick a version's Compare button…"}</option>
            </select>
            <ArrowLeftRight className="h-4 w-4 text-slate-500" />
            <select className={`${darkInput} max-w-64`} data-testid="rc-compare-old" value={compareWith} onChange={(e) => setCompareWith(e.target.value)}>
              <option value="">Compare against…</option>
              {versions.map((v) => <option key={v.id} value={v.id}>{`${v.version} (${v.status})`}</option>)}
            </select>
            {versions.map((v) => (
              <button key={v.id} onClick={() => runCompare(v.id, compareWith || (versions.find((x) => x.id !== v.id) || {}).id)}
                      data-testid={`rc-compare-${v.id}`}
                      className="rounded-lg border border-adminborder px-3 py-2 text-xs font-bold text-slate-300 hover:border-saffron hover:text-saffron">
                Compare {v.version.split("-").slice(-1)[0]}
              </button>
            ))}
          </div>
          {compare && (
            <div className="mt-4 space-y-4" data-testid="rc-compare-result">
              <div className="grid gap-3 sm:grid-cols-4">
                {[
                  ["New countries", compare.added_countries.length, "text-emerald-400"],
                  ["Removed countries", compare.removed_countries.length, "text-red-400"],
                  ["Zone changes", compare.zone_changes.length, "text-amber-400"],
                  ["Rate changes", compare.rate_diffs.length, "text-sky-400"],
                ].map(([label, n, cls]) => (
                  <div key={label} className="rounded-xl border border-adminborder bg-adminbg p-3 text-center">
                    <p className={`text-2xl font-bold ${cls}`}>{n}</p><p className="text-[10px] uppercase tracking-wider text-slate-500">{label}</p>
                  </div>
                ))}
              </div>
              {!!compare.removed_countries.length && <p className="text-xs text-red-400">Removed: {compare.removed_countries.join(", ")}</p>}
              {!!compare.added_countries.length && <p className="text-xs text-emerald-400">Added: {compare.added_countries.join(", ")}</p>}
              {!!compare.zone_changes.length && (
                <p className="text-xs text-amber-400">Zone changes: {compare.zone_changes.map((z) => `${z.country} Z${z.old_zone}→Z${z.new_zone}`).join(", ")}</p>
              )}
              {!!compare.rate_diffs.length && (
                <div className="max-h-64 overflow-auto rounded-xl border border-adminborder">
                  <table className="w-full text-xs" data-testid="rc-compare-diffs">
                    <thead><tr className="border-b border-adminborder text-left text-slate-500">
                      <th className="px-3 py-2">KG</th><th className="px-3 py-2">Zone</th><th className="px-3 py-2">Old</th><th className="px-3 py-2">New</th><th className="px-3 py-2">Change</th>
                    </tr></thead>
                    <tbody>
                      {compare.rate_diffs.slice(0, 200).map((d, i) => (
                        <tr key={i} className="border-b border-adminborder/50 last:border-0">
                          <td className="px-3 py-1.5">{d.weight}</td><td className="px-3 py-1.5">Z{d.zone}</td>
                          <td className="px-3 py-1.5">{inr(d.old_rate)}</td><td className="px-3 py-1.5">{inr(d.new_rate)}</td>
                          <td className={`px-3 py-1.5 font-bold ${d.change_pct > 0 ? "text-red-400" : d.change_pct < 0 ? "text-emerald-400" : "text-slate-400"}`}>
                            {d.change_pct == null ? "NEW/REMOVED" : `${d.change_pct > 0 ? "+" : ""}${d.change_pct}%`}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <div className={`${card} p-4`} data-testid="rc-search-card">
        <p className="mb-2 text-[11px] font-bold uppercase tracking-wider text-slate-500">Rate search — full price breakdown (admin only)</p>
        <div className="flex flex-wrap items-center gap-2">
          <input className={`${darkInput} max-w-56`} placeholder="Country (e.g. Afghanistan)" data-testid="rc-search-country"
                 value={q.country} onChange={(e) => setQ({ ...q, country: e.target.value })} />
          <input className={`${darkInput} max-w-32`} placeholder="Weight KG" type="number" step="0.1" data-testid="rc-search-weight"
                 value={q.weight} onChange={(e) => setQ({ ...q, weight: e.target.value })}
                 onKeyDown={(e) => e.key === "Enter" && search()} />
          <button onClick={search} disabled={qBusy} data-testid="rc-search-btn"
                  className="flex items-center gap-1.5 rounded-lg bg-saffron px-4 py-2.5 text-xs font-bold text-white hover:bg-saffron-hover disabled:opacity-50">
            <Search className="h-3.5 w-3.5" /> {qBusy ? "Searching…" : "Search"}
          </button>
        </div>
        {qResult && (
          <div className="mt-3 grid gap-2 sm:grid-cols-3 lg:grid-cols-6" data-testid="rc-search-result">
            {[
              ["Country / Zone", `${qResult.country} · Zone ${qResult.zone}`],
              ["Weight", `${qResult.weight} KG`],
              [`${cname} Source Rate`, `${inr(qResult.source_rate)}${qResult.rate_mode === "increment" ? "/KG" : ""}`],
              ...(qResult.war_surcharge_total ? [["War Surcharge", `${inr(qResult.war_surcharge_total)} (${inr(qResult.war_surcharge_per_kg)}/KG)`]] : []),
              ["Admin Total", inr(qResult.admin_total)],
              ["Customer Markup", inr(qResult.customer_markup)],
              ["Customer Price", inr(qResult.customer_price)],
            ].map(([label, value]) => (
              <div key={label} className="rounded-xl border border-adminborder bg-adminbg p-3">
                <p className="text-[10px] uppercase tracking-wider text-slate-500">{label}</p>
                <p className="mt-1 text-sm font-bold text-white" data-testid={`rc-search-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{value}</p>
              </div>
            ))}
            <p className="text-[10px] text-slate-600 sm:col-span-3 lg:col-span-6">Version {qResult.rate_card_version} · mode: {qResult.rate_mode}</p>
          </div>
        )}
      </div>

      {rules && (
        <div className={`${card} p-4`} data-testid="rc-rules-card">
          <button onClick={() => setRulesOpen(!rulesOpen)} data-testid="rc-rules-toggle"
                  className="flex w-full items-center justify-between text-left">
            <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500">{cname} calculation & customer pricing rules</p>
            <span className="text-xs font-bold text-saffron">{rulesOpen ? "Hide" : "Edit"}</span>
          </button>
          {rulesOpen && (
            <div className="mt-4 space-y-5">
              <div>
                <p className="mb-2 text-xs font-bold text-slate-300">Admin calculation</p>
                <div className="flex flex-wrap items-center gap-2 text-xs text-slate-400">
                  <span>0–</span>
                  <input className={`${darkInput} w-20`} type="number" data-testid="rc-rules-tmax" value={rules.table_max_weight}
                         onChange={(e) => setRules({ ...rules, table_max_weight: e.target.value })} />
                  <span>KG → exact source slab rate</span>
                </div>
                {rules.increment_tiers.map((t, i) => (
                  <div key={i} className="mt-2 flex flex-wrap items-center gap-2 text-xs text-slate-400" data-testid={`rc-rules-tier-${i}`}>
                    <span>above</span>
                    <input className={`${darkInput} w-20`} type="number" value={t.from}
                           onChange={(e) => setRules({ ...rules, increment_tiers: rules.increment_tiers.map((x, j) => j === i ? { ...x, from: e.target.value } : x) })} />
                    <span>KG{t.to == null ? "+" : " to"}</span>
                    {t.to != null && (
                      <input className={`${darkInput} w-20`} type="number" value={t.to}
                             onChange={(e) => setRules({ ...rules, increment_tiers: rules.increment_tiers.map((x, j) => j === i ? { ...x, to: e.target.value } : x) })} />
                    )}
                    <span>KG → rate at</span>
                    <input className={`${darkInput} w-20`} type="number" step="0.1" data-testid={`rc-rules-tier-${i}-key`} value={t.key_weight}
                           onChange={(e) => setRules({ ...rules, increment_tiers: rules.increment_tiers.map((x, j) => j === i ? { ...x, key_weight: e.target.value } : x) })} />
                    <span>KG row × actual KG</span>
                  </div>
                ))}
              </div>
              <div>
                <p className="mb-2 text-xs font-bold text-slate-300">Customer markup tiers</p>
                {rules.markup_tiers.map((t, i) => (
                  <div key={i} className="mt-2 flex flex-wrap items-center gap-2 text-xs text-slate-400" data-testid={`rc-rules-markup-${i}`}>
                    <span>{t.from}–{t.to == null ? "∞" : t.to} KG →</span>
                    <select className={`${darkInput} w-28`} value={t.type}
                            onChange={(e) => setRules({ ...rules, markup_tiers: rules.markup_tiers.map((x, j) => j === i ? { ...x, type: e.target.value } : x) })}>
                      <option value="fixed">Fixed ₹</option>
                      <option value="per_kg">₹ per KG</option>
                    </select>
                    <input className={`${darkInput} w-24`} type="number" data-testid={`rc-rules-markup-${i}-amount`} value={t.amount}
                           onChange={(e) => setRules({ ...rules, markup_tiers: rules.markup_tiers.map((x, j) => j === i ? { ...x, amount: e.target.value } : x) })} />
                  </div>
                ))}
              </div>
              <button onClick={saveRules} disabled={savingRules} data-testid="rc-rules-save"
                      className="rounded-lg bg-saffron px-5 py-2.5 text-xs font-bold text-white hover:bg-saffron-hover disabled:opacity-50">
                {savingRules ? "Saving…" : "Save Pricing Rules"}
              </button>
            </div>
          )}
        </div>
      )}

      {carrier === "UPS" && (
        <div className={`${card} p-4`} data-testid="rc-surge-card">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500">UPS war surcharge (₹/KG, incl. fuel + GST)</p>
              <p className="mt-0.5 text-xs text-slate-500">
                {surges.length
                  ? `${surges.length} destinations · source: ${surges[0].source_file || "manual"} · applied to the admin price before customer markup`
                  : "No surcharge imported yet — upload the UPS war surcharge Excel (Export sheet is used)."}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <input type="file" accept=".xlsx,.xls,.xlsm" data-testid="rc-surge-upload-file"
                     onChange={(e) => setSurgeFile(e.target.files?.[0] || null)}
                     className="w-52 text-xs text-slate-400 file:mr-2 file:rounded-lg file:border-0 file:bg-adminbg file:px-3 file:py-2 file:text-xs file:font-bold file:text-slate-200" />
              <button onClick={uploadSurge} disabled={surgeUploading || !surgeFile} data-testid="rc-surge-upload-submit"
                      className="flex items-center gap-1.5 rounded-lg bg-saffron px-4 py-2 text-xs font-bold text-white hover:bg-saffron-hover disabled:opacity-50">
                <FileUp className="h-3.5 w-3.5" /> {surgeUploading ? "Importing…" : "Import Surcharge"}
              </button>
            </div>
          </div>
          {!!surges.length && (
            <>
              <input className={`${darkInput} mt-3 max-w-64`} placeholder="Filter country / ISO…" data-testid="rc-surge-filter"
                     value={surgeFilter} onChange={(e) => setSurgeFilter(e.target.value)} />
              <div className="mt-3 max-h-80 overflow-auto rounded-xl border border-adminborder">
                <table className="w-full text-xs" data-testid="rc-surge-table">
                  <thead className="sticky top-0 bg-admincard">
                    <tr className="border-b border-adminborder text-left text-slate-500">
                      <th className="px-3 py-2">Country</th><th className="px-3 py-2">ISO</th><th className="px-3 py-2">Region</th>
                      <th className="px-3 py-2">Base ₹/KG</th><th className="px-3 py-2">With Fuel ₹/KG</th>
                      <th className="px-3 py-2">With GST ₹/KG (applied)</th><th className="px-3 py-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {surges.filter((s) => !surgeFilter.trim() || `${s.country_name} ${s.iso_code} ${s.region || ""}`.toLowerCase().includes(surgeFilter.trim().toLowerCase())).map((s) => (
                      <tr key={s.id} className="border-b border-adminborder/50 last:border-0" data-testid={`rc-surge-row-${s.iso_code}`}>
                        <td className="px-3 py-1.5 text-slate-200">{s.country_name || "—"}</td>
                        <td className="px-3 py-1.5 font-bold text-saffron">{s.iso_code}</td>
                        <td className="px-3 py-1.5 text-slate-400">{s.region || "—"}</td>
                        <td className="px-3 py-1.5 text-slate-400">{inr(s.surge_base)}</td>
                        <td className="px-3 py-1.5 text-slate-400">{inr(s.surge_with_fuel)}</td>
                        <td className="px-3 py-1.5">
                          <input className={`${darkInput} w-24 !px-2 !py-1`} type="number" step="0.01" min="0"
                                 data-testid={`rc-surge-input-${s.iso_code}`}
                                 value={surgeEdit[s.id] ?? s.surge_with_gst}
                                 onChange={(e) => setSurgeEdit({ ...surgeEdit, [s.id]: e.target.value })} />
                        </td>
                        <td className="px-3 py-1.5">
                          {surgeEdit[s.id] != null && parseFloat(surgeEdit[s.id]) !== s.surge_with_gst && (
                            <button onClick={() => saveSurge(s)} disabled={surgeSaving === s.id} data-testid={`rc-surge-save-${s.iso_code}`}
                                    className="rounded-lg border border-emerald-500/40 px-2.5 py-1 text-[10px] font-bold text-emerald-400 hover:bg-emerald-500/10 disabled:opacity-50">
                              {surgeSaving === s.id ? "Saving…" : "Save"}
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}

      {uploadOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-black/60 p-4" data-testid="rc-upload-modal">
          <div className={`${card} mx-auto my-16 w-full max-w-md p-6`}>
            <h3 className="font-heading text-lg font-bold text-white">Upload {cname} Rate Card</h3>
            <p className="mt-1 text-xs text-slate-500">PDF, XLSX, XLS or CSV. The file is stored as the original, parsed into a DRAFT version, and only goes live when you publish it.</p>
            <div className="mt-4 space-y-3">
              <div>
                <label className="mb-1 block text-xs font-bold text-slate-500">Effective date (e.g. 13-09-2026)</label>
                <input className={darkInput} data-testid="rc-upload-date" placeholder="DD-MM-YYYY" value={effDate} onChange={(e) => setEffDate(e.target.value)} />
              </div>
              <div>
                <label className="mb-1 block text-xs font-bold text-slate-500">Rate card file</label>
                <input type="file" accept=".pdf,.xlsx,.xls,.csv" data-testid="rc-upload-file"
                       onChange={(e) => setFile(e.target.files?.[0] || null)}
                       className="w-full text-xs text-slate-400 file:mr-2 file:rounded-lg file:border-0 file:bg-adminbg file:px-3 file:py-2 file:text-xs file:font-bold file:text-slate-200" />
              </div>
            </div>
            <div className="mt-5 flex justify-end gap-3">
              <button onClick={() => setUploadOpen(false)} data-testid="rc-upload-cancel"
                      className="rounded-lg border border-adminborder px-4 py-2 text-sm font-bold text-slate-300">Cancel</button>
              <button onClick={upload} disabled={uploading || !file} data-testid="rc-upload-submit"
                      className="flex items-center gap-2 rounded-lg bg-saffron px-5 py-2 text-sm font-bold text-white hover:bg-saffron-hover disabled:opacity-50">
                <FileUp className="h-4 w-4" /> {uploading ? "Parsing…" : "Upload & Parse"}
              </button>
            </div>
          </div>
        </div>
      )}

      {preview && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-black/60 p-4" data-testid="rc-preview-modal">
          <div className={`${card} mx-auto my-8 w-full max-w-5xl p-6`}>
            <h3 className="font-heading text-lg font-bold text-white">{cname} Rate Card Import Preview</h3>
            <div className="mt-3 grid gap-2 sm:grid-cols-3 lg:grid-cols-6" data-testid="rc-preview-summary">
              {[
                ["Version", preview.version.version],
                ["File", preview.version.file_name],
                ["Effective", preview.version.effective_date],
                ["Countries", preview.countries.length],
                ["Zones", preview.zones.length],
                ["Rate records", preview.version.total_rates],
                ["Weight slabs", preview.weights.filter((w) => w <= (rules?.table_max_weight ?? 30)).length],
                ["Increment rows", preview.weights.filter((w) => w > (rules?.table_max_weight ?? 30)).map((w) => w.toFixed(1)).join(", ") || "NONE"],
              ].map(([label, value]) => (
                <div key={label} className="rounded-xl border border-adminborder bg-adminbg p-3">
                  <p className="text-[10px] uppercase tracking-wider text-slate-500">{label}</p>
                  <p className="mt-1 truncate text-sm font-bold text-white">{value}</p>
                </div>
              ))}
            </div>

            {!!val.errors.length && (
              <div className="mt-4 rounded-xl border border-red-500/40 bg-red-500/10 p-4" data-testid="rc-preview-errors">
                <p className="text-sm font-bold text-red-400">Errors ({val.errors.length}) — publishing is blocked</p>
                <ul className="mt-2 max-h-32 list-inside list-disc space-y-1 overflow-y-auto text-xs text-red-300">
                  {val.errors.slice(0, 30).map((e, i) => <li key={i}>{e}</li>)}
                  {val.errors.length > 30 && <li>…and {val.errors.length - 30} more</li>}
                </ul>
              </div>
            )}
            {!!val.warnings.length && (
              <div className="mt-4 rounded-xl border border-amber-500/40 bg-amber-500/10 p-4" data-testid="rc-preview-warnings">
                <p className="text-sm font-bold text-amber-400">Warnings ({val.warnings.length}) — review before publishing</p>
                <ul className="mt-2 max-h-32 list-inside list-disc space-y-1 overflow-y-auto text-xs text-amber-300">
                  {val.warnings.slice(0, 30).map((w, i) => <li key={i}>{w}</li>)}
                  {val.warnings.length > 30 && <li>…and {val.warnings.length - 30} more</li>}
                </ul>
              </div>
            )}

            <div className="mt-4 grid gap-4 lg:grid-cols-2">
              <div>
                <p className="mb-2 text-[11px] font-bold uppercase tracking-wider text-slate-500">Country / Zone mapping</p>
                <div className="max-h-72 overflow-auto rounded-xl border border-adminborder" data-testid="rc-preview-countries">
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 bg-admincard"><tr className="border-b border-adminborder text-left text-slate-500">
                      <th className="px-3 py-2">Country</th><th className="px-3 py-2">ISO</th><th className="px-3 py-2">Zone</th>
                    </tr></thead>
                    <tbody>
                      {preview.countries.map((c, i) => (
                        <tr key={i} className="border-b border-adminborder/50 last:border-0">
                          <td className="px-3 py-1.5 text-slate-200">{c.country_name}</td>
                          <td className="px-3 py-1.5 text-slate-400">{c.iso_code || "—"}</td>
                          <td className="px-3 py-1.5 font-bold text-saffron">{c.zone}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <div>
                <p className="mb-2 text-[11px] font-bold uppercase tracking-wider text-slate-500">Rate table (source rates, ₹)</p>
                <div className="max-h-72 overflow-auto rounded-xl border border-adminborder" data-testid="rc-preview-rates">
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 bg-admincard"><tr className="border-b border-adminborder text-left text-slate-500">
                      <th className="px-3 py-2">KG</th>
                      {preview.zones.map((z) => <th key={z} className="px-3 py-2">Z{z}</th>)}
                    </tr></thead>
                    <tbody>
                      {preview.grid.map((g, i) => (
                        <tr key={i} className="border-b border-adminborder/50 last:border-0">
                          <td className="px-3 py-1.5 font-bold text-slate-200">{g.band || g.weight}</td>
                          {preview.zones.map((z) => (
                            <td key={z} className="px-3 py-1.5 text-slate-400">{g.rates[String(z)] ?? "—"}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <div className="mt-5 flex flex-wrap justify-end gap-3">
              <button onClick={() => setPreview(null)} data-testid="rc-preview-close"
                      className="rounded-lg border border-adminborder px-4 py-2 text-sm font-bold text-slate-300">Cancel</button>
              {preview.version.status !== "ACTIVE" && (
                <>
                  <button onClick={() => publish(preview.version.id, false)} disabled={!!val.errors.length || busy === preview.version.id}
                          data-testid="rc-preview-publish"
                          className="rounded-lg bg-emerald-600 px-5 py-2 text-sm font-bold text-white hover:bg-emerald-500 disabled:opacity-40">
                    Publish
                  </button>
                  {!!val.warnings.length && !val.errors.length && (
                    <button onClick={() => publish(preview.version.id, true)} disabled={busy === preview.version.id}
                            data-testid="rc-preview-publish-anyway"
                            className="rounded-lg bg-amber-600 px-5 py-2 text-sm font-bold text-white hover:bg-amber-500 disabled:opacity-40">
                      Publish Anyway
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
